import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { ChartDataset, TooltipCallbacks } from 'chart.js';
import { _DeepPartialObject } from 'chart.js/dist/types/utils';
import { addDays, isAfter, isBefore, isSameDay } from 'date-fns';
import { PriceService } from 'src/app/services/price/price.service';
import { PriceData } from 'src/app/services/price/price.types';
import { ComponentsModule } from 'src/app/shared/components.module';
import {
  labelTooltipCallback,
  titleTooltipCallback
} from 'src/app/tabs/forbruk/graphs/forbruk/charts/forbruk-charts-utils';
import { formatYYYYMMDDHHNumberToDate, theTime } from 'src/app/utils/date-utils';

@Component({
  selector: 'app-prices-forbruk-dashboard',
  standalone: true,
  imports: [CommonModule, IonicModule, ComponentsModule],
  template: `
    <ion-row>
      <ion-col>
        @if (!hasError) {
        <p class="mt-0">
          Spotprisen og energileddet i nettleia varierer fra time til time. Merk
          at denne grafen kun viser deler av totalregnestykket, og at påslag og
          avgifter kommer i tillegg.
        </p>
        <p>
          Kan du flytte noe forbruk fra de dyreste timene for å redusere
          kostnadene dine?
        </p>
        @if (objectid && dataSets) {
        <ion-checkbox
          mode="md"
          labelPlacement="end"
          [checked]="showStromstotte"
          (ionChange)="toggleStromstotte()"
          >Vis pris med strømstøtte</ion-checkbox
        >
        <app-bar-chart
          [dataSets]="dataSets"
          [labels]="labels"
          [title]="title"
          [tooltipCallbacks]="tooltipCallbacks"
          [stacked]="true"
          chartId="prices-forbruk-dashboard-chart"
          yUnit="øre/kWh"
          xUnit="hour"
          [showYTitle]="false"
          [showLegend]="true"
          [yMax]="maxY"
          [animationDisabled]="true"
          [majorTicks]="true"
        ></app-bar-chart
        >}} @else {
        <p class="bg-[--warning-40] p-4 rounded-lg m-0">
          Det skjedde dessverre en feil når vi prøvde å hente priser. Prøv igjen
          senere.
        </p>
        }
      </ion-col>
    </ion-row>
  `
})
export class PricesForbrukDashboardComponent {
  @Input() objectid: string;

  prices: PriceData[];
  hasTomorrowSpot = false;
  hasError = false;
  showStromstotte = true;

  dataSets: ChartDataset<'bar'>[];
  labels: Date[] | string[];
  title: string;
  tooltipCallbacks: _DeepPartialObject<TooltipCallbacks<'bar'>>;
  maxY: number;

  constructor(private priceService: PriceService) { }

  ngOnChanges() {
    if (this.objectid) {
      this.fetchData();
    }
  }

  fetchData() {
    this.hasError = false;
    const now = theTime();
    const period = +now.get("DD") === 1
      ? now.goes({ MM: -1 }).to(['YYYY', 'MM'])
      : now.to(['YYYY', 'MM']);

    this.priceService.getPricesHourPeriod(this.objectid, period).subscribe({
      next: (res) => {
        if (res) {
          this.prices = res;
          this.getChartData();
        } else {
          this.hasError = true;
        }
      },
      error: (err) => {
        this.hasError = true;
      }
    });
  }

  getChartData() {
    this.dataSets = [];

    const pricesValid = this.prices.filter(
      (x) => (x.spot || x.fast) && x.nettleie
    );
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const latestPeriod: Date = pricesValid.reduce(
      (acc, curr) =>
        // Time is formatted 1-24 instead of 0-23, so we subtract one hour
        isAfter(formatYYYYMMDDHHNumberToDate(curr.period - 1), acc)
          ? formatYYYYMMDDHHNumberToDate(curr.period - 1)
          : acc,
      formatYYYYMMDDHHNumberToDate(pricesValid[0].period - 1)
    );
    if (!isSameDay(latestPeriod, today) && isAfter(latestPeriod, today)) {
      this.hasTomorrowSpot = true;
    }

    const latestPrices = pricesValid.filter(
      (x) =>
        isAfter(formatYYYYMMDDHHNumberToDate(x.period), today) &&
        isBefore(formatYYYYMMDDHHNumberToDate(x.period - 1), addDays(today, 2))
    );
    if (latestPrices.length === 0) {
      this.hasError = true;
    }
    this.maxY = Math.max(
      ...latestPrices.map(
        (x) =>
          ((x.fast ?? x.spot ?? 0) + (x.paslag ?? 0) + (x.nettleie ?? 0)) * 100
      )
    );

    this.title =
      'Forbrukspriser i dag' + (this.hasTomorrowSpot ? ' og i morgen' : '');

    this.tooltipCallbacks = {
      label: (tt) => labelTooltipCallback(tt, 'øre/kWh'),
      title: (tt) => titleTooltipCallback(tt, 'hour', 'øre/kWh')
    };

    this.labels = latestPrices.map((x) =>
      formatYYYYMMDDHHNumberToDate(x.period - 1)
    );

    // Problem with offset
    // this.dataSets.push({
    //   label: 'Totalpris uten strømstøtte',
    //   stack: 'B',
    //   data: latestPrices.map(
    //     (x) => (x.nettleie + (x.fast ?? x.spot ?? 0)) * 100
    //   ),
    //   backgroundColor: 'rgba(0, 41, 135, 0.3)',
    //   borderColor: 'rgba(0, 41, 135, 0.3)',
    //   borderWidth: 0
    // });

    this.dataSets.push({
      label: 'Energiledd nettleie',
      // stack: 'A',
      data: latestPrices.map((x) => (x.nettleie ?? 0) * 100),
      backgroundColor: '#26b41b',
      borderColor: '#26b41b',
      borderWidth: 0
    });

    this.dataSets.push({
      label: 'Strømpris',
      // stack: 'A',
      data: latestPrices.map((x) => {
        if (this.showStromstotte && x.stromstotte) {
          return ((x.fast ?? x.spot ?? 0) - x.stromstotte) * 100;
        } else {
          return (x.fast ?? x.spot ?? 0) * 100;
        }
      }),
      backgroundColor: '#002987',
      borderColor: '#002987',
      borderWidth: 0
    });

    // if (sumNum(latestPrices.map((x) => x.paslag)) > 0) {
    //   this.dataSets.push({
    //     label: 'Påslag strøm',
    //     data: latestPrices.map((x) => (x.paslag ?? 0) * 100),
    //     backgroundColor: '#ff6c00',
    //     borderColor: '#ff6c00',
    //     borderWidth: 0
    //   });
    // }

    if (this.labels.length === 0 || this.dataSets.length === 0) {
      this.hasError = true;
    }
  }

  toggleStromstotte() {
    this.showStromstotte = !this.showStromstotte;
    this.getChartData();
  }
}
